import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

// List of sub-sections for the Astrology Department
const departments = [
    { name: "Introduction", key: "about" },
    { name: "Offered Courses", key: "courses" },
    { name: "Faculty Members", key: "faculty" },
    { name: "Timetable", key: "timetable" },
    { name: "Facilities", key: "facilities" },
    { name: "Seminars/Conferences/Workshops", key: "events" },
    { name: "Research Scholars", key: "researchScholars" },
    { name: "Achievements", key: "achievements" },
    { name: "Contact", key: "contact" },
];

// Content for each section
const departmentContent = {
    about: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Introduction</h4>
            <p className="mb-3">
                Under Vedic literature, Vedas and Vedangas are studied. Samhitas,
                Brahmanas, Aranyakas, and Upanishads are known as Vedic heritage.
                Among the six Vedangas - Shiksha, Kalpa, Vyakarana, Nirukta, Chhanda,
                and Jyotisha are included, with Jyotisha being considered the eye of
                the Veda Purusha: <b>"Jyotisham Netramuchyate"</b> (Astrology is said to
                be the eye). Just as it is impossible for a person to know and
                understand anything without eyes, similarly, knowledge of Vedic
                literature is impossible without knowledge of Jyotisha. Knowledge of
                time is possible only through Jyotisha Shastra:{" "}
                <b>"Kalavidhayakam Shastram Jyotishashastram"</b> (Jyotisha Shastra is
                the science that determines time). This is because everything is
                contained within time, as it is said:{" "}
                <b>"Kaladheenam Jagat Sarvam"</b> (The whole world is dependent on time).
                In Jyotisha Shastra, eighteen pioneering acharyas initiated the study
                of Jyotisha Shastra. The eminent scholar of Jyotisha Shastra,
                Kapisthala (Kaithal), who lived from 505 to 587 AD, divided Jyotisha
                Shastra into three branches: <b>Siddhanta, Samhita, and Hora</b>.
                Under the Siddhanta branch, mathematical and spherical analytical
                study of planets is done. Under the Samhita branch, the auspicious and
                inauspicious results of the elements of the entire universe are
                determined. Under the Hora branch, the auspicious and inauspicious
                results occurring in the past, future, and present of a native are
                determined based on the position of planets at the time of birth or
                question horoscope. This is also called Jataka Shastra or Predictive
                Astrology. Under Jyotisha Shastra, authentic texts such as
                Suryasiddhanta, Brihajjataka, Laghujataka, Saravali, Jatakaparijata,
                Laghuparashari, Muhurtachintamani, Brihatparasharahora Shastra,
                Hastasanjeevan, Sachitra Samudrik Rahasya, Bharatiya Kundali Vigyan,
                Janmapatradeepaka, Tajikanilakanthi, etc., are studied.
            </p>
            <p className="mb-3">
                At Maharshi Valmiki Sanskrit University, the Department of Astrology
                offers Diploma in Astrology (one-year), Bachelor of Honours (Shastri
                Hons.) in Astrology (four-year), and Postgraduate (Acharya) (two-year)
                courses. Additionally, a Ph.D. program (Vidyavaridhi) in Astrology is
                also offered for research. The objective of these courses is to
                familiarize the general public with the rare and profound knowledge of
                Astrology and make it socially useful.
            </p>
        </div>
    ),
    courses: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Offered Courses</h4>
            <p className="mb-3">The courses offered by the Department of Astrology are as follows:</p>
            <ul className="list-unstyled">
                <li className="mb-2">
                    <i className="bi bi-book-fill me-2"></i> Diploma
                </li>
                <li className="mb-2">
                    <i className="bi bi-book-fill me-2"></i> Shastri Honours (UG Hons.)
                </li>
                <li className="mb-2">
                    <i className="bi bi-book-fill me-2"></i> Acharya (PG)
                </li>
                <li className="mb-2">
                    <i className="bi bi-book-fill me-2"></i> Vidyavaridhi (Ph.D.)
                </li>
            </ul>

            <h5 className="mt-4 mb-3 text-secondary">Course Admission Eligibility</h5>
            <ul>
                <li className="mb-2">
                    **Astrology Diploma Course is a one-year program** divided into two
                    semesters. Admission to the Diploma course usually takes place
                    every year in <b>June-July</b>. The eligibility for admission to the
                    Astrology Diploma course is passing Senior Secondary Certificate
                    10+2 in <b>Arts, Commerce, or Science stream</b> or an equivalent
                    examination from a recognized board.
                </li>
                <li className="mb-2">
                    **Shastri Honours (Astrology) Course is a four-year program**
                    divided into eight semesters. Admission to this course usually
                    takes place every year in <b>June-July</b>. The eligibility for
                    admission to the Shastri Honours (Astrology) course is passing
                    Senior Secondary Certificate 10+2 in <b>Arts, Commerce, or Science
                        stream</b> or an equivalent examination from a recognized board.
                </li>
                <li className="mb-2">
                    **Acharya (Astrology) Course is a two-year program** divided into
                    four semesters. Admission to this course usually takes place
                    every year in <b>June-July</b>. The eligibility for admission to the
                    Acharya (Astrology) course is passing Arts, Commerce, or Science
                    stream or an equivalent examination from any university/college.
                </li>
                <li className="mb-2">
                    **Vidyavaridhi (Ph.D.) Course** in Astrology focuses on new
                    findings regarding complex and socially relevant topics in
                    Astrology. The minimum eligibility for admission to this research-oriented
                    course is passing Acharya/M.A. in Astrology with <b>55% marks</b>.
                    Admission to this course is once a year through an entrance
                    examination. This is a minimum 3-year and maximum 6-year
                    exploratory and research-based program.
                </li>
            </ul>
        </div>
    ),
    faculty: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Faculty Members</h4>
            <div className="table-responsive">
                <table className="table table-bordered table-striped table-hover align-middle">
                    <thead className="table-primary text-center">
                        <tr>
                            <th>Photo</th>
                            <th>Teacher's Name</th>
                            <th>Post</th>
                            <th>Highest Qualification</th>
                            <th>Contact</th>
                            <th>Email</th>
                        </tr>
                    </thead>
                    <tbody className="text-center">
                        <tr>
                            <td style={{ width: "140px" }}>
                                <img
                                    src={require("../assets/Photos/डॉ. नरेश शर्मा (विभागाध्यक्ष, ज्योतिष विभाग).jpg")}
                                    alt="Dr. Naresh Sharma"
                                    className="img-fluid rounded"
                                    style={{ height: "120px", objectFit: "cover" }}
                                />
                            </td>
                            <td>Dr. Naresh Sharma</td>
                            <td>Head of Department, Assistant Professor</td>
                            <td>Ph.D.</td>
                            <td>70828 11234</td>
                            <td>
                                <a href="mailto:hodjyotish@mvsu.ac.in">hodjyotish@mvsu.ac.in</a>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <img
                                    src={require("../assets/Photos/डॉ. नवीन शर्मा (ज्योतिष विभाग).jpg")}
                                    alt="Dr. Naveen Sharma"
                                    className="img-fluid rounded"
                                    style={{ height: "120px", objectFit: "cover" }}
                                />
                            </td>
                            <td>Dr. Naveen Sharma</td>
                            <td>Assistant Professor</td>
                            <td>Ph.D.</td>
                            <td>9650834736</td>
                            <td>
                                <a href="mailto:astronaveen999@gmail.com">astronaveen999@gmail.com</a>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <img
                                    src={require("../assets/Photos/डॉ चन्द्रकान्त (ज्योतिष विभाग).jpg")}
                                    alt="Dr. Chandra Kant"
                                    className="img-fluid rounded"
                                    style={{ height: "120px", objectFit: "cover" }}
                                />
                            </td>
                            <td>Dr. Chandra Kant</td>
                            <td>Assistant Professor</td>
                            <td>Ph.D., J.R.F./NET</td>
                            <td>70187 81438</td>
                            <td>
                                <a href="mailto:Drcks.mvsu@gmail.com">Drcks.mvsu@gmail.com</a>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <img
                                    src={require("../assets/Photos/डॉ. गोविन्द बल्लभ (ज्योतिष विभाग).JPG")}
                                    alt="Dr. Govind Ballabh"
                                    className="img-fluid rounded"
                                    style={{ height: "120px", objectFit: "cover" }}
                                />
                            </td>
                            <td>Dr. Govind Ballabh</td>
                            <td>Assistant Professor</td>
                            <td>Ph.D., NET</td>
                            <td>70115 04209</td>
                            <td>
                                <a href="mailto:gballabh92k@gmail.com">gballabh92k@gmail.com</a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    ),

    timetable: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Timetable</h4>
            <p className="mb-3">
                The departmental timetable for Astrology is prepared at the beginning
                of each session according to the curriculum.
            </p>
        </div>
    ),
    facilities: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Facilities</h4>
            <ul className="mb-3">
                <li className="mb-2">Practical knowledge of Astrology for students.</li>
                <li className="mb-2">Practical knowledge of Palmistry.</li>
                <li className="mb-2">Knowledge of Muhurta and Panchang creation.</li>
                <li className="mb-2">
                    Imparting scientific aspects of characteristics, dreams, omens,
                    Svara, etc.
                </li>
                <li className="mb-2">
                    Clearly explaining the concepts of previous birth and rebirth
                    based on Karma Vipaka.
                </li>
                <li className="mb-2">
                    Kundali analysis through Astrological Applications.
                </li>
                <li className="mb-2">Guidance for competitive examinations.</li>
                <li className="mb-2">
                    Providing experience to students for becoming astrological
                    consultants.
                </li>
            </ul>
        </div>
    ),
    events: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Seminars / Conferences / Workshops / Lecture Series</h4>
            <ul className="mb-3">
                <li className="mb-2">
                    A one-day national seminar on the topic{" "}
                    <b>"Role of Teachers in the Implementation of National Education
                        Policy"</b> was organized on <b>07.02.2021</b> at 11 AM, with Dr.
                    Naresh Sharma (Head of Astrology Department) as the convener.
                </li>
                <li className="mb-2">
                    A <b>"Pre-Examination Evaluation Workshop (Suryasiddhanta /
                        Laghuparashari)"</b> was organized from <b>22.02.2021 to 28.02.2021</b>,
                    with Dr. Naresh Sharma (Head of Astrology Department) as the
                    convener.
                </li>
                <li className="mb-2">
                    A one-day practical workshop on the topic{" "}
                    <b>"Palabha-Parigyanam"</b> was organized by the Department of
                    Astrology on <b>21.03.2021 (Equinox Day)</b>, with Dr. Naresh
                    Sharma (Head of Astrology Department) as the convener.
                </li>
                <li className="mb-2">
                    An online lecture was delivered by Prof. Deviprasad Tripathi as the
                    main speaker on the topic{" "}
                    <b>"Relevance of Astrology and Vastu Shastra in Present Times"</b>{" "}
                    on <b>25.08.2021</b> at 04 PM. Dr. Naresh Sharma (Head of Astrology
                    Department) was the moderator.
                </li>
                <li className="mb-2">
                    An online lecture was delivered by Dr. Naresh Sharma as the main
                    speaker on the topic{" "}
                    <b>"Astrology is Direct and Scientific"</b> on <b>24.09.2021</b> at
                    11 AM.
                </li>
                <li className="mb-2">
                    On the occasion of National Mathematics Day on <b>24.09.2021</b>, Dr.
                    Naresh Sharma delivered a lecture as the main speaker on the topic{" "}
                    <b>"Practical Importance of Mathematics"</b>.
                </li>
                <li className="mb-2">
                    A one-day national workshop was organized both online and offline
                    on <b>18.10.2024</b> for <b>"Diwali Festival Date Determination"</b>{" "}
                    for the year 2024, in which professors from{" "}
                    <b>15 universities</b> and representatives from{" "}
                    <b>14 states</b> of India participated. Dr. Naresh Sharma, Head of
                    the Astrology Department, was the convener.
                </li>
                <li className="mb-2">
                    A two-day national workshop was organized both online and offline
                    on the topic <b>"Sukshmadik Sadhanam Panchangamandanamcha"</b> from{" "}
                    <b>20-21, March 2023</b>, in which <b>15 Panchang manufacturers</b>{" "}
                    and <b>63 scholars</b> from all over India participated. Dr. Naresh
                    Sharma, Head of the Astrology Department, was the convener.
                </li>
                <li className="mb-2">
                    A three-day national workshop on{" "}
                    <b>"Curriculum and Study Material Creation"</b> was organized both
                    online and offline from <b>14-16, April 2023</b>, in which{" "}
                    <b>24 professors and scholars</b> from{" "}
                    <b>10 universities</b> across India participated. Dr. Naresh Sharma,
                    Head of the Astrology Department, was the convener.
                </li>
                <li className="mb-2">
                    A three-day national seminar on{" "}
                    <b>"Indian Knowledge Tradition in the context of National
                        Education Policy 2020"</b> was organized both online and offline
                    from <b>05-07, May 2023</b>, in which more than{" "}
                    <b>100 scholars</b> from all over India participated. Dr. Naresh
                    Sharma, Head of the Astrology Department, was the convener.
                </li>
            </ul>
        </div>
    ),
    researchScholars: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Research Scholars</h4>
            <ul className="mb-3">
                <li className="mb-2">Shri Dinesh Kumar, Research Scholar, Department of Astrology</li>
            </ul>
        </div>
    ),
    achievements: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Achievements</h4>
            <ul className="mb-3">
                <li className="mb-2">
                    Sachin, a student of Acharya Jyotish session 2020-22, selected for
                    the post of Religious Teacher (JCO) in the Indian Army.
                </li>
                <li className="mb-2">
                    Dhaval Sharma, a student of Acharya Jyotish session 2020-22,
                    qualified UGC-NET examination.
                </li>
                <li className="mb-2">
                    Naveen, a student of Acharya Jyotish session 2021-23, qualified
                    UGC-NET examination.
                </li>
            </ul>
        </div>
    ),
    contact: (
        <div className="mb-4">
            <h4 className="mb-3 text-primary">Contact</h4>
            <div className="table-responsive mb-4">
                <table className="table table-bordered table-striped table-hover align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th scope="col">S. No.</th>
                            <th scope="col">Name</th>
                            <th scope="col">Post</th>
                            <th scope="col">Contact</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>Dr. Naresh Sharma</td>
                            <td>Head of Department</td>
                            <td>70828 11234</td>
                        </tr>
                        <tr>
                            <td>2.</td>
                            <td>Dr. Naveen Sharma</td>
                            <td>Assistant Professor (Contractual)</td>
                            <td>9650834736</td>
                        </tr>
                        <tr>
                            <td>3</td>
                            <td>Dr. Chandra Kant</td>
                            <td>Assistant Professor (Contractual)</td>
                            <td>70187 81438</td>
                        </tr>
                        <tr>
                            <td>4</td>
                            <td>Dr. Govind Ballabh</td>
                            <td>Assistant Professor (Contractual)</td>
                            <td>70115 04209</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h5 className="mb-3 mt-4 text-secondary">Departmental Non-Academic Staff</h5>
            <div className="table-responsive mb-4">
                <table className="table table-bordered table-striped table-hover align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th scope="col">S. No.</th>
                            <th scope="col">Name</th>
                            <th scope="col">Post</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1.</td>
                            <td>Shri Sita Ram</td>
                            <td>Clerk</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    ),
};

export default function AstrologyDepartment_Eng() {
    const [showSidebar, setShowSidebar] = useState(true);
    const [activeSectionKey, setActiveSectionKey] = useState("about"); // Default to "About Department"

    const handleSectionClick = (key) => {
        setActiveSectionKey(key);
        // Hide sidebar on click for small screens
        if (window.innerWidth < 768) {
            setShowSidebar(false);
        }
    };

    return (
        <div className="d-flex vh-100 flex-column flex-md-row bg-light">
            {/* Toggle button for small screens */}
            <button
                className="btn btn-primary d-md-none m-2 shadow-sm"
                onClick={() => setShowSidebar(!showSidebar)}
            >
                ☰ Menu
            </button>

            {/* Sidebar */}
            <aside
                className={`bg-white text-dark p-3 shadow-lg d-flex flex-column ${showSidebar ? "d-block" : "d-none"
                    } d-md-flex col-md-3 border-end`}
            >
                <h5 className="text-center fw-bold mb-4 text-dark border-bottom pb-2">
                    Department of Astrology
                </h5>
                <div className="flex-grow-1 overflow-auto pe-2">
                    {departments.map((dept, index) => (
                        <p
                            key={index}
                            className={`mb-2 p-2 rounded small cursor-pointer ${activeSectionKey === dept.key
                                ? "bg-primary text-white shadow-sm"
                                : "bg-light text-dark hover-effect"
                                }`}
                            onClick={() => handleSectionClick(dept.key)}
                            style={{
                                cursor: "pointer",
                                transition: "background-color 0.3s ease, color 0.3s ease",
                            }}
                        >
                            {dept.name}
                        </p>
                    ))}
                </div>
            </aside>

            {/* Content Area */}
            <main className="flex-grow-1 p-4 bg-white shadow col-md-9 overflow-auto">
                {departmentContent[activeSectionKey]}
            </main>

            {/* CSS for hover effect */}
            <style jsx>{`
        .hover-effect:hover {
          background-color: #e2e6ea !important;
          color: #000 !important;
        }
      `}</style>
        </div>
    );
}